import {
  MantineReactTable,
  useMantineReactTable,
  MRT_GlobalFilterTextInput,
  MRT_ShowHideColumnsButton,
} from 'mantine-react-table'
import { ActionIcon as MantineActionButton } from '@mantine/core'
import { PenIcon, Trash, FileText, KeyRound } from 'lucide-react'
import { FontAwesome } from '@/widgets/ui/font-awesome'
import { useUsersTable } from '../../lib/use-users-table'
import { FC, ReactNode } from 'react'
import { UserListItem } from '@/shared/api/topli/TopliApi'
import { useTranslation } from 'react-i18next'
import { getLocalization } from '@/widgets/LanguageSelect/lib/consts'
import { useNavigate } from 'react-router-dom'
import { handleCopy } from '@/shared/helpers/helpers'
import { notification } from '@/shared/helpers/notificationMessages'
import { useSubscribers, useUpdateSubscriber } from '@/entities/Subscribers'
import { useMemo } from 'react'
import { Select } from '@mantine/core'
import type { MRT_ColumnDef } from 'mantine-react-table'

type UsersTableProps = {
  isAdmin: boolean
  currentUserId?: number
  addButtonSlot: ReactNode
  onOpenEditModal: (row: UserListItem) => void
  onOpenDeleteModal: (row: UserListItem) => void
  onOpenChangePasswordModal: (row: UserListItem) => void
  handleOpenPresentationsModal: (row: UserListItem) => void
}

export const UsersTable: FC<UsersTableProps> = props => {
  const {
    isAdmin,
    currentUserId,
    addButtonSlot,
    onOpenEditModal,
    onOpenDeleteModal,
    handleOpenPresentationsModal,
    onOpenChangePasswordModal,
  } = props
  const {
    search,
    pagination,
    columns,
    sorting,
    fetchedUsers,
    totalRowCount,
    isErrorUsers,
    isFetchingUsers,
    isLoadingUsers,
    setPagination,
    setSorting,
    handleSearchChange,
    columnVisibility,
    setColumnVisibility,
    columnFilters,
    setColumnFilters,
  } = useUsersTable()
  const { t, i18n } = useTranslation(['userManagement', 'common'])
  const localization = getLocalization(i18n.language)
  const navigate = useNavigate()
  const { data: subscriberList } = useSubscribers({})
  const updateSubscriberMutation = useUpdateSubscriber()

  // Check if current user is an admin subscriber
  const currentUserSubscriber = useMemo(() => {
    if (!currentUserId || !subscriberList?.data) return null
    return subscriberList.data.find(item => item.user?.id === currentUserId)
  }, [currentUserId, subscriberList])

  const canManageSubscriberRoles = useMemo(() => {
    return currentUserSubscriber && (currentUserSubscriber as any).role === 'admin'
  }, [currentUserSubscriber])

  // Add column for managing subscriber roles
  const columnsWithSubscriberRole = useMemo<MRT_ColumnDef<UserListItem>[]>(() => {
    const subscriberRoleColumn: MRT_ColumnDef<UserListItem> = {
      accessorKey: 'subscriber_role',
      header: t('userManagement:subscriber_role', { defaultValue: 'Subscriber Role' }),
      enableColumnFilter: false,
      enableSorting: false,
      Cell: ({ row }) => {
        const userSubscriber = subscriberList?.data?.find(item => item.user?.id === row.original.id)

        if (!userSubscriber) {
          return <div className='text-sm text-gray-400'>-</div>
        }

        const currentRole = (userSubscriber as any).role || 'manager'

        return (
          <Select
            name={`subscriber_role_${row.original.id}`}
            value={currentRole}
            onChange={async value => {
              if (!value || value === currentRole) return

              try {
                await updateSubscriberMutation.mutateAsync({
                  itemId: userSubscriber.id,
                  data: {
                    telegram_id: userSubscriber.telegram_id,
                    firstname: userSubscriber.firstname,
                    lastname: userSubscriber.lastname,
                    is_subscribed: userSubscriber.is_subscribed,
                    is_authenticated: userSubscriber.is_authenticated,
                    user_id: userSubscriber.user_id ?? userSubscriber.user?.id ?? null,
                    role: value as 'admin' | 'manager',
                  } as any,
                })
                notification.success(
                  t('common:notification_titles.success'),
                  t('userManagement:subscriber_role_updated', { defaultValue: 'Role updated' }),
                )
              } catch (error) {
                notification.error(
                  t('common:notification_titles.error'),
                  t('userManagement:subscriber_role_update_error', {
                    defaultValue: 'Failed to update role',
                  }),
                )
              }
            }}
            data={[
              { value: 'admin', label: t('userManagement:admin', { defaultValue: 'Admin' }) },
              { value: 'manager', label: t('userManagement:manager', { defaultValue: 'Manager' }) },
            ]}
            disabled={updateSubscriberMutation.isPending}
            size='sm'
            styles={{
              input: {
                minHeight: '2rem',
                fontSize: '0.875rem',
              },
            }}
          />
        )
      },
    }

    return canManageSubscriberRoles ? [...columns, subscriberRoleColumn] : columns
  }, [columns, canManageSubscriberRoles, subscriberList, t, updateSubscriberMutation])
  const handleCopyTelegramLink = (hashSlug: string | null | undefined) => {
    if (!hashSlug) {
      notification.error(
        t('common:notification_titles.error'),
        t('userManagement:telegram_link_error'),
      )
      return
    }
    const telegramLink = `${import.meta.env.VITE_TELEGRAM_BASE_URL}${hashSlug}`
    handleCopy(telegramLink)
    notification.success(
      t('common:notification_titles.success'),
      t('userManagement:telegram_link_copied'),
    )
  }

  const table = useMantineReactTable({
    columns: columnsWithSubscriberRole,
    data: fetchedUsers,
    enableStickyHeader: false,
    enableEditing: true,
    enableColumnActions: false,
    manualPagination: true,
    manualSorting: true,
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    rowCount: totalRowCount,
    positionActionsColumn: 'first',
    localization,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnFiltersChange: setColumnFilters,
    enableColumnFilters: true,
    manualFiltering: true,
    columnFilterDisplayMode: 'popover',
    mantineSearchTextInputProps: {
      placeholder: t('userManagement:search'),
      value: search || '',
      onChange: e => handleSearchChange(e.target.value),
    },
    onGlobalFilterChange: handleSearchChange,
    renderRowActions: ({ row }) => {
      const isDisabledActionButtons = !isAdmin && currentUserId !== row.original.id
      const isSubscribed =
        subscriberList?.data?.some(item => item.user?.id === row.original.id) ?? false

      // Show Telegram button if:
      // 1. Current user is an admin subscriber (sees for all)
      // 2. Or this is the current user's row (sees only own)
      const showTelegramButton = canManageSubscriberRoles || currentUserId === row.original.id

      return (
        <div className='flex flex-row space-x-1 pr-8'>
          <MantineActionButton
            onClick={() => handleOpenPresentationsModal(row.original)}
            className='text-blue bg-blue/10 hover:bg-blue hover:text-white rounded-full transition-all'
            variant='light'
          >
            <FileText size={16} />
          </MantineActionButton>
          <MantineActionButton
            onClick={() => {
              currentUserId === row.original.id
                ? navigate('/profile')
                : onOpenEditModal(row.original)
            }}
            className='text-blue bg-blue/10 hover:bg-blue hover:text-white rounded-full transition-all'
            variant='light'
            disabled={isDisabledActionButtons}
          >
            <PenIcon size={16} />
          </MantineActionButton>
          <MantineActionButton
            onClick={() => onOpenChangePasswordModal(row.original)}
            className='text-blue bg-blue/10 hover:bg-blue hover:text-white rounded-full transition-all'
            variant='light'
            disabled={isDisabledActionButtons}
          >
            <KeyRound size={16} />
          </MantineActionButton>
          {showTelegramButton && (
            <MantineActionButton
              onClick={() => handleCopyTelegramLink(row.original.hash_slug)}
              className='bg-blue hover:bg-blue/80 rounded-full transition-all flex items-center justify-center'
              variant='filled'
              disabled={isSubscribed}
              title={
                isSubscribed
                  ? t('userManagement:already_subscribed')
                  : t('userManagement:copy_telegram_link')
              }
            >
              <FontAwesome iconClass='fa-solid fa-paper-plane' size='sm' color='white' />
            </MantineActionButton>
          )}
          <MantineActionButton
            onClick={() => onOpenDeleteModal(row.original)}
            className='text-red bg-red/10 hover:bg-red hover:text-white rounded-full transition-all'
            variant='light'
            disabled={!isAdmin}
          >
            <Trash size={16} />
          </MantineActionButton>
        </div>
      )
    },

    renderTopToolbar: ({ table }) => {
      return (
        <div className='flex flex-row items-center justify-between mb-2'>
          <MRT_GlobalFilterTextInput table={table} />
          <div className='flex flex-row items-center space-x-2'>
            <MRT_ShowHideColumnsButton className='h-[2.5rem] w-[2.5rem] rounded-md' table={table} />
            {addButtonSlot}
          </div>
        </div>
      )
    },
    mantineToolbarAlertBannerProps: isErrorUsers
      ? {
          color: 'red',
          children: 'Error loading data',
        }
      : undefined,
    mantinePaperProps: {
      shadow: 'none',
      withBorder: false,
    },
    mantineTableProps: {
      sx: {
        tableLayout: 'auto',
      },
    },
    mantineTableBodyProps: {
      sx: {
        '&: td': {
          minWidth: 90,
        },
        '& tr:hover': {
          backgroundColor: '#FFFFFF',
        },
      },
    },
    mantineTableBodyRowProps: {
      className: 'hover:bg-[#F4FAFD]/10',
    },
    state: {
      isLoading: isLoadingUsers,
      pagination,
      sorting,
      showGlobalFilter: true,
      showAlertBanner: isErrorUsers,
      showProgressBars: isFetchingUsers,
      columnVisibility,
      columnFilters,
    },
  })

  return <MantineReactTable table={table} />
}
